import { Box, Button, Card, Container, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo, useCallback, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import Label from "src/components/label";
import Iconify from "src/components/iconify/iconify";
import { enqueueSnackbar } from "notistack";
import { DownloadExcelButton, ReportRunner, useDateRangeFilter } from "../runner/report-runner";

type UnregisteredFuelingLine = {
	truckName: string,
	driverName: string,
	date: string,
	time: string,
	location: string,
	fuelGallons: number,
	amountBilled: number,
	fuelBefore: number | null,
	fuelAfter: number | null,
	status: string,
	occurredAt: string,
}

function StatusLabel({ status }: { status: string }) {
  if (status === "Reconciled") {
    return <Label variant="filled" color="success">Reconciled</Label>;
  }
  if (status === "Not Reconciled") {
    return <Label variant="filled" color="error">Not Reconciled</Label>;
  }
  return <Label variant="filled" color="warning">Unverifiable</Label>;
}

type UnregisteredFuelingReportLinesProps = {
  lines: UnregisteredFuelingLine[],
}

const UnregisteredFuelingReportLines = memo(({ lines }: UnregisteredFuelingReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines, "occurredAt", "desc");

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="truckName">
                Truck
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="driverName">
                Driver
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="location">
                Location
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="fuelGallons">
                Gallons
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="amountBilled">
                Amount
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="status">
                Status
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.truckName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.driverName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.date}
                </TruxTableCell>
                <TruxTableCell>
                  {line.time}
                </TruxTableCell>
                <TruxTableCell>
                  {line.location}
                </TruxTableCell>
                <TruxTableCell>
                  {line.fuelGallons.toFixed(2)}
                </TruxTableCell>
                <TruxTableCell>
                  ${line.amountBilled.toFixed(2)}
                </TruxTableCell>
                <TruxTableCell>
                  <StatusLabel status={line.status} />
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div>;
});

type PageModel = {
  lines?: UnregisteredFuelingLine[],
  availability?: {
    hasIntegration: boolean,
  },
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' };

type DemoTransaction = {
  truck: string,
  tankBefore: number,
  tankAfter: number,
  tankDelta: string,
  billed: string,
  merchant: string,
  matched: boolean,
  verdict: string,
}

const DEMO_TRANSACTIONS: DemoTransaction[] = [
  {
    truck: 'Truck #214',
    tankBefore: 38,
    tankAfter: 84,
    tankDelta: '+91.2 gal in tank',
    billed: '92.3 gal · $312.41',
    merchant: 'Pilot Travel Center · Amarillo, TX',
    matched: true,
    verdict: 'Reconciled within 1.1 gal',
  },
  {
    truck: 'Truck #108',
    tankBefore: 71,
    tankAfter: 71,
    tankDelta: '+0.0 gal in tank',
    billed: '46.8 gal · $158.90',
    merchant: "Love's #291 · Joplin, MO",
    matched: false,
    verdict: 'Flagged: fuel never reached the tank',
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    title: 'Driver fuels up',
    body: 'Nothing to log and no receipts to photograph. The stop happens like it always has.',
  },
  {
    title: 'The truck reports the rise',
    body: 'The ELD reads tank level over OBD2/J1939 diagnostics before and after the stop, down to the gallon.',
  },
  {
    title: 'We reconcile in the background',
    body: 'Every provider transaction is matched to a real tank increase. Anything off lands in this report.',
  },
];

function TankGauge({ before, after }: { before: number, after: number }) {
  return <Box sx={{ position: 'relative', height: 8, maxWidth: 180, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${before}%`, bgcolor: 'grey.400' }} />
    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${before}%`, width: `${after - before}%`, bgcolor: 'success.main' }} />
  </Box>;
}

function ReconciliationDemoRow({ transaction }: { transaction: DemoTransaction }) {
  const { truck, tankBefore, tankAfter, tankDelta, billed, merchant, matched, verdict } = transaction;

  return <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
    alignItems: 'center',
    gap: 2,
    px: 2.5,
    py: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  }}>
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.disabled">{truck}</Typography>
      <Typography variant="body2" sx={{ ...mono, fontWeight: 600 }}>
        {tankBefore}% → {tankAfter}%
        <Box component="span" sx={{ ml: 1.5, color: matched ? 'success.main' : 'text.disabled' }}>
          {tankDelta}
        </Box>
      </Typography>
      <TankGauge before={tankBefore} after={tankAfter} />
    </Stack>

    <Box sx={{
      order: { xs: 3, md: 0 },
      justifySelf: { xs: 'start', md: 'center' },
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      borderRadius: 4,
      typography: 'caption',
      fontWeight: 700,
      color: matched ? 'success.dark' : 'error.dark',
      bgcolor: (theme) => alpha(matched ? theme.palette.success.main : theme.palette.error.main, 0.16),
    }}>
      <Iconify icon={matched ? 'eva:checkmark-circle-2-fill' : 'eva:alert-triangle-fill'} width={16} />
      {verdict}
    </Box>

    <Stack spacing={0.75} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
      <Typography variant="caption" color="text.disabled">{merchant}</Typography>
      <Typography variant="body2" sx={{ ...mono, fontWeight: 600 }}>{billed}</Typography>
    </Stack>
  </Box>;
}

export function NoFuelCardIntegrationFound() {
  return <Card sx={{ p: { xs: 3, md: 5 } }}>
    <Stack spacing={5}>
      <Stack spacing={1.5} alignItems="center" textAlign="center">
        <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 2 }}>
          Unregistered Fueling
        </Typography>
        <Typography variant="h3">
          Your trucks already meter every gallon
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
          The ELD in the diagnostics port reads fuel level straight from the engine over
          OBD2/J1939, so we know exactly how much fuel enters every tank. Connect your fuel
          card and we reconcile each transaction against that reading, automatically, in the
          background.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1fr auto 1fr', gap: 2, px: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Read from the truck · OBD2 / J1939
          </Typography>
          <Box />
          <Typography variant="overline" color="text.secondary" textAlign="right">
            Billed by the fuel card
          </Typography>
        </Box>

        {DEMO_TRANSACTIONS.map((transaction) => (
          <ReconciliationDemoRow key={transaction.truck} transaction={transaction} />
        ))}

        <Typography variant="caption" color="text.disabled" textAlign="center">
          Example transactions
        </Typography>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
        {HOW_IT_WORKS_STEPS.map((step, index) => (
          <Stack key={step.title} spacing={0.5}>
            <Typography variant="subtitle2" sx={{ ...mono, color: 'success.main' }}>
              {`0${index + 1}`}
            </Typography>
            <Typography variant="subtitle1">{step.title}</Typography>
            <Typography variant="body2" color="text.secondary">{step.body}</Typography>
          </Stack>
        ))}
      </Box>

      <Box sx={{
        borderRadius: 2,
        p: { xs: 3, md: 4 },
        textAlign: 'center',
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
      }}>
        <Typography variant="h6" gutterBottom>
          Connect your fuel card
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 2.5 }}>
          Ask your fuel card provider for API access and CC support@countingtrucks.com and
          sales@countingtrucks.com in that email, or just talk to your sales representative.
        </Typography>
        <Button
          variant="contained"
          color="success"
          size="large"
          href="mailto:sales@countingtrucks.com?cc=support@countingtrucks.com&subject=Fuel%20Card%20Integration"
        >
          Contact Sales
        </Button>
      </Box>
    </Stack>
  </Card>;
}

export default function Page() {
  const settings = useSettingsContext();

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    availability: axios.get(
      endpoints.reports.unregisteredFueling.availability
    ).then(response => response.data),
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.unregisteredFueling.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data.lines),
  }), []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const dateRangeFilter = useDateRangeFilter();

  const filters = [
    dateRangeFilter
  ];

  const hasIntegration = data?.availability?.hasIntegration;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {hasIntegration && (
          <PageHeader title="Unregistered Fueling">
            <DownloadExcelButton baseUrl={endpoints.reports.unregisteredFueling.excel} filters={filters} currentRequest={currentRequest} />
          </PageHeader>
        )}

        {loaded && data && !hasIntegration && <NoFuelCardIntegrationFound />}

        {hasIntegration && (
          <ReportRunner
            filters={filters}
            onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
            onRun={(request) => {
              currentRequest.current = request;
              setShouldRefresh(true);
            }}
            loading={loading}
          />
        )}

        {hasIntegration && data && (data.lines?.length || 0) > 0 && <UnregisteredFuelingReportLines lines={data.lines || []} />}
        {hasIntegration && loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container>
  );
}
